import { Product } from "~/domains/product/Product";
import { Box, CardContent, Stack, styled } from "~/common/mui";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { type ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useTranslation } from "react-i18next";
import { isNonNull } from "~/common/utils/reducers";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";

import { Parameter, ParameterStack } from "~/common/components/ParameterV2";
import { getTariffNumbers } from "~/utils/productUtils";
import { getCountriesList } from "~/domains/product/utils/productDescriptorUtils";

interface CartEntrySummaryProps {
  product: ProductDescriptor;
  editMode: boolean;
}

export const OptionalPartsEntrySummary = ({ product }: CartEntrySummaryProps) => {
  const { t } = useTranslation();

  const params = [
    ProductParams2.SchaefflerId({ product, t }),
    {
      label: "Tariff number",
      value: getTariffNumbers(product.base).join(", "),
    },
    {
      label: "Country of origin",
      value: getCountriesList({ product: product.base }).join(", "),
    },
  ].filter((p): p is { label: string; value: string } => isNonNull(p) && !!p.value);

  return (
    <ProductDescriptorContext value={product}>
      <CardSummary sx={{ position: "relative" }}>
        <Box sx={{ gridArea: "image" }}>
          <Product.PdpLink>
            <Product.PrimaryImageWithLogosV2 size={64} logoWidth={32} gap={8} />
          </Product.PdpLink>
        </Box>

        <Stack gap={2} sx={{ gridArea: "summary" }}>
          <Stack gap={0.5}>
            <Product.DesignationContainer>
              <Product.PreferredIndicator />
              <Product.PdpLink>
                <Product.DesignationV2 variant="titleMedium" fontWeight={"bold"} />
              </Product.PdpLink>
            </Product.DesignationContainer>
            <Product.MMDChip />
            <Product.ShortDescriptionV2 />
          </Stack>

          <ParameterStack parameterVariant={"row"}>
            {params.map((data) => (
              <Parameter {...data} />
            ))}
          </ParameterStack>
          <Product.ReconditionableTag />
        </Stack>
      </CardSummary>
    </ProductDescriptorContext>
  );
};

const CardSummary = styled(CardContent)(({ theme: _theme }) => ({
  padding: "24px",
  display: "grid",
  columnGap: "32px",
  rowGap: "24px",
  gridTemplateAreas: `"image summary     quantity    lines          subtotal"`,
  gridTemplateColumns: "104px 30fr 3fr 3fr 4fr",
}));
